// Shared client for the DashAI admin Firebase Cloud Functions.
//
// Every admin screen used to hand-roll its own `adminFetch`/`campaignFetch`
// wrapper: read the Firebase ID token, set Authorization + Content-Type, POST,
// check `res.ok`, parse JSON. Those copies all bypassed the canonical
// `postWithAuthRetry` helper, so none of them refreshed a stale token on a 401
// or sent the `X-Client-Version` header. `callAdminFunction` is the single
// place that talks to an admin function, so every screen gets the 401-refresh
// retry and the version header for free.

import { postWithAuthRetry } from './onePSfaClient';
import { getFunctionsBase } from './functionsBase';

/**
 * POST `body` to the admin Cloud Function `fnName` and return its parsed JSON.
 *
 * Routes through `postWithAuthRetry`, which attaches the Firebase ID token +
 * `X-Client-Version` header and retries once with a force-refreshed token on a
 * 401. On any non-2xx response, throws an `Error` carrying the backend's
 * `error` message when present, else a status-coded fallback.
 *
 * Defaults the result type to `any` to match the `Response.json()` the
 * per-screen wrappers returned before consolidation (callers already index
 * into the result); pass an explicit `<T>` to opt into a typed response.
 *
 * @param fnName Firebase function name, e.g. `dashaiAdminUserList`.
 * @param body   JSON request body (defaults to `{}`).
 * @param signal Optional AbortSignal for cancellation.
 */
 
export async function callAdminFunction<T = any>(
  fnName: string,
  body: Record<string, unknown> = {},
  signal?: AbortSignal,
): Promise<T> {
  const base = getFunctionsBase();
  const res = await postWithAuthRetry(`${base}/${fnName}`, body, signal);
  if (!res.ok) {
    const err = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(err.error || `Request failed (${res.status})`);
  }
  return res.json() as Promise<T>;
}
